import { Injectable } from '@angular/core';
import amplitude from 'amplitude-js';
import { ShapeList, TextTypes } from '@fb-tool-front/overlay-editor/models/overlay-editor.model';
import { ClientSubscriptionTypes } from '@fb-tool-front/shared/constants/auth.constants';
import { PixelService } from 'ngx-pixel';

export type RequestDemoModalCaller =
  | 'requestDemo'
  | 'goToVideos'
  | 'uploadModal'
  | 'autoVideosUpdate'
  | 'applyOverlay'
  | 'overlaysPriority'
  | 'catalogSettings'
  | 'editProductSet'
  | 'deleteProductSet'
  | 'createProductSet'
  | 'applyVideoTemplate';

export const TRACKABLE_EVENTS = {
  //shared
  visit: () => ['visit'],
  loginWithFacebook: () => ['login with Facebook clicked'],
  signUpWithFacebook: () => ['sign up with Facebook clicked'],
  openRequestDemoModal: (payload: {
    subscription: ClientSubscriptionTypes;
    caller: RequestDemoModalCaller;
  }) => ['request demo modal clicked', payload],
  submitRequestDemoModal: () => ['request demo modal submitted'],
  openSettingsPage: () => ['open settings page'],
  visitHome: () => ['home visit'],
  homeMenuItemClicked: () => ['home click'],
  overlaysMenuItemClicked: () => ['overlays click'],
  videoMenuItemClicked: () => ['videos click'],
  getFullAccessClicked: () => ['get full access in header click'],
  changeLanguage: (payload: { lang: string }) => ['change lang', payload],
  deleteAccount: () => ['delete account clicked'],
  switchPlan: () => ['switch plan'],

  // onboarding
  letStartDemoClicked: () => ["home let's start click"],
  letStartOverlaysClicked: () => ["home let's start click overlays"],
  letStartVideosClicked: () => ["home let's start click videos"],
  onboardingStepOneNextClicked: () => ['onboarding step 1 - next click'],
  onboardingStepOneSkipClicked: () => ['onboarding step 1 - skip click'],
  onboardingStepOneAvatarClicked: () => ['onboarding step 1 - avatar click'],
  onboardingStepTwoChooseCatalogsClicked: () => ['onboarding step 2 - catalog dropdown click'],
  onboardingStepTwoCatalogClicked: () => ['onboarding step 2 - select catalog click'],
  onboardingStepTwoNextClicked: () => ['onboarding step 2 - next click'],
  onboardingStepThreeOverlayClicked: () => ['onboarding step 3 - overlay click'],
  onboardingStepThreeLetsStartClicked: () => ['onboarding step 3 - lets start click'],

  // catalogs
  overlaysCompleteTutor: () => ['product tour - completed click'],
  overlaysCancelTutor: () => ['product tour - skip tour click'],
  overlaysOpenPriorityModal: () => ['overlays - overlay priority settings click'],
  overlaysDropdownClicked: () => ['overlays - catalog dropdown click'],
  overlaysOpenCatalogSettingsModal: () => ['overlays - catalog settings click'],
  overlaysCreateFeedClicked: (payload: { originalFeedId: string; originalFeedUrl: string }) => [
    'overlays - create feed clicked',
    payload
  ],
  overlaysRemoveFeedClicked: (payload: { originalFeedId: string; originalFeedUrl: string }) => [
    'overlays - remove feed clicked',
    payload
  ],
  overlaysCreateOverlayClicked: () => ['overlays - new overlay click'],
  overlaysNewOverlayFromScratchClicked: () => ['overlays - new overlay from scratch click'],
  overlaysNewOverlayFromTemplateClicked: (payload: { templateId: string }) => [
    'overlays - new overlay from template click',
    payload
  ],
  overlaysEditOverlayClicked: (payload: { templateId: string }) => [
    'overlays - edit overlay clicked',
    payload
  ],
  overlaysOverlayPreviewClicked: () => ['overlays - overlay preview click'],
  overlaysReloadProductsClicked: () => ['overlays - reload products click'],
  overlaysHelpClicked: () => ['overlays - help click'],
  overlaysCopyOverlayClicked: () => ['overlays - copy overlay click'],
  overlaysDeleteOverlayClicked: () => ['overlays - delete overlay click'],
  overlaysCreateProductSetClicked: () => ['overlays - create product set click'],
  overlaysEditProductSetClicked: (payload: { id: string }) => [
    'overlays - edit product set click',
    payload
  ],
  overlaysDeleteProductSetClicked: (payload: { id: string }) => [
    'overlays - delete product set click',
    payload
  ],

  // editor
  editorUndoClicked: () => ['editor undo clicked'],
  editorRedoClicked: () => ['editor redo clicked'],
  editorCloneOverlayClicked: () => ['editor clone overlay clicked'],
  editorCreateOverlayClicked: () => ['editor new overlay click'],
  editorNewOverlayFromScratchClicked: () => ['editor new overlay clicked (from scratch)'],
  editorNewOverlayFromTemplateClicked: (payload: { templateId: string }) => [
    'editor new overlay clicked (from template)',
    payload
  ],
  editorEditOverlayClicked: (payload: { templateId: string }) => [
    'overlays - edit overlay click',
    payload
  ],
  editorRemoveOverlayClicked: () => ['editor remove overlay clicked'],
  editorTextAdded: () => ['editor text added'],
  editorImportPSD: () => ['overlays - import layers from PSD click'],
  editorImportFigma: () => ['overlays - import layers from Figma click'],
  editorAddImageClicked: () => ['editor add image clicked'],
  editorShapeAdded: (payload: { type: ShapeList }) => ['editor shape added', payload],
  editorScaleToFullScreenClicked: () => ['scale_to_full_screen overlay editor clicked'],
  editorCutAndCenterClicked: () => ['cut_and_center overlay editor clicked'],
  editorCutAndFitClicked: () => ['cut_and_fit overlay editor clicked'],
  editorDynamicFieldAdded: (payload: { type: TextTypes }) => [
    'editor dynamic field added',
    payload
  ],
  editorFieldChanged: (payload: { field: 'width' | 'height' | 'top' | 'left' }) => [
    'editor field changed - size and position',
    payload
  ],
  editorTrimClicked: () => ['editor trim clicked'],
  editorTrimAndFitClicked: () => ['editor trim and fit clicked'],
  editorRemoveImageBgEnabled: () => ['editor remove image bg'],
  editorGridClicked: (payload: { value: boolean }) => ['editor grid clicked', payload],
  editorOverlayCreated: (payload: { id: string; title: string }) => [
    'editor overlay created',
    payload
  ],
  editorApplyOverlay: (payload: { overlayId: string; productSetId: string; catalogId: string }) => [
    'editor apply overlay clicked',
    payload
  ],
  editorDisableOverlay: (payload: {
    overlayId: string;
    productSetId: string;
    catalogId: string;
  }) => ['editor disable overlay clicked', payload],
  editorAddGradientClicked: () => ['editor add gradient clicked'],

  //video
  videoBulkDownloadVideoClicked: (payload: { ids: string[] }) => [
    'bulk download video clicked',
    payload
  ],
  videoDownloadVideoClicked: (payload: { id: string }) => ['download video clicked', payload],
  videoRenderVideoClicked: (payload: Record<string, unknown>) => ['render video clicked', payload],
  videoSaveVideoClicked: (payload: { type: string; url: string; name: string }) => [
    'save video clicked',
    payload
  ],
  videoDancingVideoClicked: () => ['Create dancing video click'],
  videoAdaptedVideoClicked: () => ['Create adapted video click'],
  videoFeedBasedCreateClicked: () => ['Create feed-based video click'],
  videoFeedBasedCreateVideosClicked: (payload: { value: number }) => [
    'Feed-based video - create videos',
    payload
  ],
  videoAssetBasedCreateClicked: () => ['Create Asset-based video click'],
  videoAssetBasedCreateVideosClicked: (payload: { value: number }) => [
    'Asset-based video - create videos',
    payload
  ],
  videoGoogleSheetCreateClicked: () => ['Create Asset-based video (google sheet) click'],
  videoGoogleSheetCreateVideosClicked: (payload: { url: string }) => [
    'Asset-based video (google sheet) - create videos',
    payload
  ],

  // create overlay
  createOverlayImportFromPsd: () => ['create overlay modal - import from psd'],
  createOverlayImportFromFigma: () => ['create overlay modal - import from figma']
};

@Injectable({
  providedIn: 'root'
})
export class MetricsService {
  private readonly prefix = 'App';

  constructor(private pixel: PixelService) {}

  public trackEvent(trackableEvents: any[]): void {
    const [eventName, eventOptions] = trackableEvents;

    amplitude.getInstance().logEvent(`${this.prefix} ${eventName}`, eventOptions);
    this.pixel.trackCustom(`${this.prefix} ${eventName}`, eventOptions);
  }
}
